import _ from "underscore";
import { useEffect } from "react";
import cropr, { CropfieldAdviceModel, DiseaseAdviceModel } from "../../shared/utils/cropr";
import { useSFAMBackbone } from "../../shared/hooks/useSFAMBackbone";

export type ActionType = "immediate" | "consider" | "possible" | "no" | "killed" | "missing";
const getDiseaseStatusColor = (group: ActionType): string => {
  let color = "#DFE5E7";
  switch (group) {
    case "no":
      color = "#15C386";
      break;
    case "immediate":
      color = "#FF5630";
      break;
    case "consider":
      color = "#FFA11C";
      break;
    case "possible":
      color = "#13C9DE";
      break;
    case "missing":
      color = "#708090";
      break;
  }
  return color;
};
const getAdviceAction = (advice: DiseaseAdviceModel): ActionType => {
  const action =
    advice.mostUrgentAction().action ||
    (advice.getAdvice().warnings ? advice.getAdvice().warnings[0] : "OK");

  if (advice.mostUrgentAction().action) {
    switch (action) {
      case "RECOMMENDED":
        return "immediate";
      case "CONSIDER":
        return "consider";
      case "RISK":
      case "HIGH_RISK":
        return "possible";
      case "NOT_NECESSARY":
        return "no";
      default:
        return "no";
    }
  }
  if (advice.getAdvice().warnings) {
    if (_.includes(["CROP_KILLED", "CEASED_TO_EXIST"], advice.getAdvice().warnings[0]))
      return "killed";
    return "missing";
  }
  // default 'ok'
  return "no";
};
export type DiseaseContent = {
  color: string;
  label: string;
  value: number;
  action: ActionType;
};
export type DiseaseStatusType = {
  counter: {
    total: number;
    attention: number;
  };
  data: {
    sortOrder: string;
    content: DiseaseContent[];
  };
  cropsPerAction: {
    [at in ActionType]?: { [key: string]: Set<number> };
  };
};
const getSFAMDiseaseStatus = (cropFieldAdvices: CropfieldAdviceModel[]): DiseaseStatusType => {
  // copied from diseaseView.js and modified.
  let result = {
    counter: {
      total: 0,
      attention: 0,
    },
    data: {
      sortOrder: "value-desc",
      content: [] as DiseaseContent[],
    },
    cropsPerAction: {} as {
      [at in ActionType]?: { [key: string]: Set<number> };
    },
  };

  const groupedActionCounts = _.countBy(
    _.flatten(
      _.map(cropFieldAdvices, (cropfieldAdvice) => {
        return cropfieldAdvice.get("advices").models.map((advice: DiseaseAdviceModel) => {
          const action = getAdviceAction(advice);
          const cf = _.find(
            cropr.cropfields.models,
            (cf) => cf.get("did") === advice.get("cropFieldId")
          );
          const crop = cf && cf.getRelated("crop");
          if (!result.cropsPerAction[action]) result.cropsPerAction[action] = {};
          if (!result.cropsPerAction[action]![crop.id])
            result.cropsPerAction[action]![crop.id] = new Set();
          cf && result.cropsPerAction[action]![crop.id].add(cf.id);
          return action;
        });
      })
    )
  );

  result.data.content = (Object.entries(groupedActionCounts) as Array<[ActionType, number]>).map(
    ([action, count]) => {
      if (action !== "no") result.counter.attention += count;
      result.counter.total += count;

      return {
        color: getDiseaseStatusColor(action),
        label: count.toString(),
        value: count,
        action: action,
      } as DiseaseContent;
    }
  );

  return result;
};

export const useDiseaseStatus = (): DiseaseStatusType | undefined => {
  const [, cropfieldAdvices] = useSFAMBackbone<CropfieldAdviceModel>("cropFieldAdvices");
  useEffect(() => {
    // @ts-ignore
    cropr.sfam.advices.fetchAdvices();
  }, []);
  return cropfieldAdvices && getSFAMDiseaseStatus(cropfieldAdvices.models);
};
